import React, { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { imgFull } from "../lib/imgProxy";

/**
 * Fullscreen lightbox — mobile-first, robust close, swipe, keyboard.
 * - Tap anywhere OUTSIDE the image closes the lightbox
 * - Explicit close X button (always visible, large tap target)
 * - Swipe left/right on the image to navigate
 * - Keyboard: Esc, ←, →
 * - Body scroll lock with guaranteed cleanup
 */
export const Lightbox = ({ photos, startIndex = 0, propertyName = "", onClose }) => {
  const [idx, setIdx] = useState(startIndex);
  const [loaded, setLoaded] = useState(false);
  const touchStart = useRef(null);
  const touchEnd = useRef(null);

  const goPrev = useCallback((e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    setLoaded(false);
    setIdx((i) => (i - 1 + photos.length) % photos.length);
  }, [photos.length]);

  const goNext = useCallback((e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    setLoaded(false);
    setIdx((i) => (i + 1) % photos.length);
  }, [photos.length]);

  const handleClose = useCallback((e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    onClose();
  }, [onClose]);

  // Keyboard
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") goPrev();
      else if (e.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [goPrev, goNext, onClose]);

  // Body scroll lock with GUARANTEED cleanup
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev || "";
    };
  }, []);

  // Preload adjacent images (best-effort)
  useEffect(() => {
    if (photos.length <= 1) return;
    [(idx + 1) % photos.length, (idx - 1 + photos.length) % photos.length].forEach((i) => {
      const img = new Image();
      img.src = imgFull(photos[i]);
    });
  }, [idx, photos]);

  const onTouchStart = (e) => {
    if (e.touches.length !== 1) return;
    touchStart.current = e.touches[0].clientX;
    touchEnd.current = null;
  };
  const onTouchMove = (e) => {
    if (e.touches.length !== 1) return;
    touchEnd.current = e.touches[0].clientX;
  };
  const onTouchEndHandler = () => {
    if (touchStart.current == null || touchEnd.current == null) {
      touchStart.current = null; touchEnd.current = null;
      return;
    }
    const delta = touchStart.current - touchEnd.current;
    if (Math.abs(delta) > 50 && photos.length > 1) {
      if (delta > 0) goNext();
      else goPrev();
    }
    touchStart.current = null;
    touchEnd.current = null;
  };

  const current = imgFull(photos[idx]);

  return createPortal((
    <div
      className="fixed inset-0 z-[100] bg-black/95 animate-fadeIn overflow-hidden"
      onClick={handleClose}
      role="dialog"
      aria-modal="true"
      aria-label={propertyName}
      data-testid="lightbox-overlay"
      style={{ touchAction: "none" }}
    >
      {/* Close button — always visible and large */}
      <button
        type="button"
        onClick={handleClose}
        aria-label="Close"
        data-testid="lightbox-close"
        className="fixed top-4 right-4 sm:top-6 sm:right-6 z-[110] w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-white text-[hsl(var(--ink))] flex items-center justify-center shadow-lg active:scale-95 transition-transform"
      >
        <X className="w-6 h-6 sm:w-7 sm:h-7" strokeWidth={2.5} />
      </button>

      {/* Prev / Next */}
      {photos.length > 1 && (
        <>
          <button
            type="button"
            onClick={goPrev}
            aria-label="Previous"
            data-testid="lightbox-prev"
            className="fixed left-2 sm:left-6 top-1/2 -translate-y-1/2 z-[110] w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-white/95 hover:bg-white text-[hsl(var(--ink))] flex items-center justify-center shadow-lg active:scale-95 transition-transform"
          >
            <ChevronLeft className="w-6 h-6 sm:w-7 sm:h-7" strokeWidth={2.5} />
          </button>
          <button
            type="button"
            onClick={goNext}
            aria-label="Next"
            data-testid="lightbox-next"
            className="fixed right-2 sm:right-6 top-1/2 -translate-y-1/2 z-[110] w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-white/95 hover:bg-white text-[hsl(var(--ink))] flex items-center justify-center shadow-lg active:scale-95 transition-transform"
          >
            <ChevronRight className="w-6 h-6 sm:w-7 sm:h-7" strokeWidth={2.5} />
          </button>
        </>
      )}

      {/* Image container — click OUTSIDE image closes; only the img itself blocks propagation */}
      <div
        className="absolute inset-0 flex items-center justify-center px-0 sm:px-20 pt-16 pb-16 sm:pt-4 sm:pb-14"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEndHandler}
      >
        {!loaded && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-10 h-10 rounded-full border-2 border-white/30 border-t-white animate-spin" />
          </div>
        )}
        <img
          key={current}
          src={current}
          alt={`${propertyName} — ${idx + 1}/${photos.length}`}
          data-testid="lightbox-image"
          onLoad={() => setLoaded(true)}
          onError={() => setLoaded(true)}
          onClick={(e) => e.stopPropagation()}
          className={`max-w-full max-h-full w-auto h-auto object-contain select-none transition-opacity duration-200 ${loaded ? 'opacity-100' : 'opacity-0'}`}
          style={{ WebkitUserDrag: "none", userSelect: "none", maxHeight: "calc(100vh - 8rem)", maxWidth: "100%" }}
          draggable={false}
        />
      </div>

      {/* Counter */}
      {photos.length > 1 && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[110] px-4 py-1.5 rounded-full bg-black/60 text-white/90 text-xs sm:text-sm backdrop-blur">
          <span data-testid="lightbox-counter">{idx + 1} / {photos.length}</span>
        </div>
      )}
    </div>
  ), document.body);
};
